import { Observable } from 'rxjs';
import { filter, map, share } from 'rxjs/operators';
import notNullish from '@tv2/lib-core/notNullish';
import * as Tracking from '../../modules/dataProviders/Tracking';
import { ClientPlatform, type Media, UnifiedPlayer } from '../../types';
import { PlayUnifiedLiveTrackingQuery } from '../../modules/dataProviders/__generated__/Tracking';
import { PlayUnifiedLiveEpgQuery } from '../../modules/dataProviders/__generated__/Epg';
import { UnifiedPlayerPlugin } from '../../core/UnifiedPlayer';

function mapMediaToTrackingMedia(media: Media) {
  return {
    playback: {
      tracking: {
        ...(media.tracking?.nielsen && {
          nielsen: media.tracking?.nielsen,
        }),
        ...(media.tracking?.adobe && {
          adobe: media.tracking?.adobe,
        }),
      },
    },
    entity: {
      type: media.metadata?.isLive ? 'station' : 'episode', // good enough
      guid: media.metadata?.guid,
      contentProvider: media.metadata?.contentProvider ?? media.metadata?.channel,
      duration: media.metadata?.duration ?? Infinity,
    },
    metadata: media.metadata,
  };
}
export type UseTrackingMappedMedia = ReturnType<typeof mapMediaToTrackingMedia>;

export const makeMediaObservable$ = (player: UnifiedPlayer) =>
  player
    .observe<Media>(data => data.media)
    .pipe(
      filter(media => Boolean(media?.src)),
      filter(media => Boolean(media?.metadata)),
      map(media => mapMediaToTrackingMedia(media)),
      share()
    );

interface Destroyable {
  destroy(): void;
}

export interface TrackingServices {
  player: UnifiedPlayer;
  media$: ReturnType<typeof makeMediaObservable$>;
  liveTracking$: Observable<PlayUnifiedLiveTrackingQuery>;
  liveEpg$?: Observable<PlayUnifiedLiveEpgQuery>;
  epgOptions: UseTrackingProps['epgOptions'];
}

export type V2PluginFactory = (services: TrackingServices) => Destroyable;

export interface UseTrackingProps {
  epgOptions: {
    endpoint: string;
    clientPlatform?: ClientPlatform;
  };
  plugins: V2PluginFactory[];
}

/**
 * Plugin function for use with UnifiedPlayer.
 */
export const trackingPlugin = (options: UseTrackingProps): UnifiedPlayerPlugin => {
  let instance: TrackingPlugin | null;
  const plugin = (_media$: Observable<Media>, player: UnifiedPlayer) => {
    instance = new TrackingPlugin(_media$, player, options);
    return instance;
  };

  plugin.destroy = () => instance?.destroy();

  return plugin;
};

class TrackingPlugin {
  #pluginsToDestroy: Destroyable[] = [];

  constructor(
    _media$: Observable<Media>,
    player: UnifiedPlayer,
    { epgOptions, plugins }: UseTrackingProps
  ) {
    // TODO: Question: Why to we need to take media from unified player and not use the media passed in?
    const media$ = makeMediaObservable$(player);

    const nielsenTrackingVersion = '2';

    const trackingPoll$ = Tracking.poll$(media$ as any, epgOptions, nielsenTrackingVersion);
    const liveTracking$ = trackingPoll$.pipe(
      filter(x => Boolean(x.liveTrackingResult)),
      map(x => x.liveTrackingResult),
      filter(notNullish)
    );

    const services: TrackingServices = {
      player,
      media$,
      liveTracking$,
      epgOptions,
    };

    (plugins || []).forEach(pluginFactory => {
      const plugin = pluginFactory(services);
      this.#pluginsToDestroy.push(plugin);
    });
  }

  destroy() {
    this.#pluginsToDestroy.forEach(instance => {
      instance.destroy();
    });
  }
}
